import Link from '@/components/AppLink';
import type { EnrollPlatform } from "./enroll-platforms.const";
import type { EnrollInstructionStep } from "../_types/enroll.types";

const DevicesLink = () => <Link href="/devices">Devices</Link>;

export const ENROLL_INSTRUCTIONS_BY_PLATFORM: Record<EnrollPlatform, EnrollInstructionStep[]> = {
  macos: [
    {
      title: "Step 1: Install the management profile",
      body: (
        <>
          On the Mac, open the enrollment link we sent you (or download the{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            enrollment package (.mobileconfig)
          </a>
          ) and double-click to install the MDM profile.
        </>
      ),
      callout: (
        <>
          If macOS blocks the profile, open <strong>System Settings → Privacy &amp; Security</strong>{" "}
          and choose <strong>Open Anyway</strong> for the downloaded profile.
        </>
      ),
    },
    {
      title: "Step 2: Approve MDM enrollment",
      body: (
        <>
          In <strong>System Settings → Privacy &amp; Security</strong>, approve the device management
          payload. Sign in with your work account if prompted.
        </>
      ),
    },
    {
      title: "Step 3: Complete setup",
      body: (
        <>
          Grant permissions for configuration profiles, network access, and background management as
          requested. After enrollment finishes, required apps and policies will sync automatically.
        </>
      ),
    },
    {
      title: "Step 4: Start managing",
      body: (
        <>
          When the Mac shows as enrolled, return here and open <DevicesLink /> to verify status,
          groups, and policies.
        </>
      ),
    },
  ],
  windows: [
    {
      title: "Step 1: Download the Windows agent",
      body: (
        <>
          On the PC, download the latest{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            Vetty Device Agent (64-bit)
          </a>{" "}
          installer from your enrollment email or this page.
        </>
      ),
      callout: (
        <>
          Need an offline install? Use the{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            standalone MSI package
          </a>{" "}
          on a machine without internet during setup.
        </>
      ),
    },
    {
      title: "Step 2: Run the installer",
      body: (
        <>
          Run the installer as administrator. When asked, sign in with your organization account or
          paste the enrollment code from the invite.
        </>
      ),
    },
    {
      title: "Step 3: Allow Windows Security prompts",
      body: (
        <>
          Approve Windows Security, SmartScreen, or Defender prompts so the agent can register the
          device and stay in sync with policy updates.
        </>
      ),
    },
    {
      title: "Step 4: Start managing",
      body: (
        <>
          After the agent reports success, open <DevicesLink /> to confirm the PC is online and
          receiving policies.
        </>
      ),
    },
  ],
  linux: [
    {
      title: "Step 1: Install the enrollment package",
      body: (
        <>
          Add our package repository (or download the{" "}
          <a href="#" onClick={(e) => e.preventDefault()}>
            .deb / .rpm
          </a>
          ) and install the device agent using the command from your enrollment invite.
        </>
      ),
      callout: (
        <>
          Air-gapped server? Copy the offline bundle from the portal and install with{" "}
          <code>sudo dpkg -i</code> or <code>sudo rpm -i</code> per the included README.
        </>
      ),
    },
    {
      title: "Step 2: Register the device",
      body: (
        <>
          Run <code>vetty-agent enroll</code> (or use the GUI wizard) and provide your organization ID
          or scan the QR code shown in the dashboard invite.
        </>
      ),
    },
    {
      title: "Step 3: Enable the background service",
      body: (
        <>
          Start and enable the agent systemd service so check-ins and policy updates run
          automatically after reboot.
        </>
      ),
    },
    {
      title: "Step 4: Start managing",
      body: (
        <>
          Once the host appears as enrolled, use <DevicesLink /> to monitor compliance and assigned
          policies.
        </>
      ),
    },
  ],
};
